import { motion, useReducedMotion } from 'framer-motion';
import { Clock } from 'lucide-react';

import { useInitialLoadInFlight, useSlowLoading } from '../hooks/useSlowLoading';
import { STROKE, SURFACE, TEXT } from '../theme/tokens';

// FD-596 — the one place the console says a load is slow.
//
// Mounted ONCE, in `AppShell`, rather than taught to each page's loading state.
// The console has a dozen of them (DataTable skeleton rows, `QueryGate`, the
// groups spinner, detail skeletons, the route loader) and a cold start reaches
// all of them; a line added to each would be a dozen chances to drift, and a page
// built next month would not have it. Reading React Query's own in-flight state
// covers every page that loads through it, including ones that do not exist yet.
//
// THE COPY IS WORDED SO IT IS TRUE EITHER WAY. The console cannot tell a cold
// start from a slow request — both are a pending fetch with no answer yet — so it
// does not assert that the service IS waking up. It says what it knows (still
// working) and the one explanation an operator needs (an idle service takes up
// to a minute), and stops there.
//
// It sits at the BOTTOM of the content column, above the route loader's overlay
// (z-20), as a single calm line on a white pill — not a banner, not an alert, and
// no status colour: nothing is wrong, and amber here would teach operators to
// read a cold start as a fault, which is the misreading this task exists to fix.

export const SLOW_LOAD_MESSAGE = 'Still working. An idle service can take up to a minute to wake up.';

export interface SlowLoadNoticeProps {
  /**
   * Treat the console as loading regardless of query state. `AppShell` sets it
   * while the landing gate waits on the environment probe (FD-586): that wait is
   * a `whoami` call outside React Query, against a Manager that is just as
   * likely to be cold.
   */
  hold?: boolean;
}

export default function SlowLoadNotice({ hold = false }: SlowLoadNoticeProps = {}) {
  const loading = useInitialLoadInFlight();
  const slow = useSlowLoading(loading || hold);
  const reduceMotion = useReducedMotion();

  return (
    // The live region is ALWAYS mounted, so assistive tech announces the line
    // when it appears; a region mounted together with its text is often missed.
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none sticky bottom-4 z-30 flex justify-center px-8"
    >
      {/* No exit animation, deliberately: the line must be gone the moment the page
          has loaded, not fading out over a populated table. It fades IN only. */}
      {slow && (
        <motion.div
          key="slow-load"
          data-testid="slow-load-notice"
          initial={reduceMotion ? false : { opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0, transition: { duration: reduceMotion ? 0 : 0.2 } }}
          className="flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[12px]"
          style={{
            backgroundColor: SURFACE.base,
            border: `1px solid ${STROKE.base}`,
            color: TEXT.secondary,
          }}
        >
          <Clock size={14} strokeWidth={1.5} aria-hidden="true" />
          <span>{SLOW_LOAD_MESSAGE}</span>
        </motion.div>
      )}
    </div>
  );
}
